import type { PaletteCommand, PaletteSection } from './commandPalette.types';
import { NAV_THEMES, type NavTheme } from './nav-tokens';
import PaletteRow from './PaletteRow';
import type { SearchStatus } from './search/use-palette-sections';

export const NO_MATCHES_MESSAGE = 'No matches. Try a different query.';
export const SEARCH_FAILED_MESSAGE = "Search isn't available right now. Try again in a moment.";

interface PaletteResultsProps {
  listId: string;
  sections: PaletteSection[];
  status: SearchStatus;
  activeId: string | null;
  theme: NavTheme;
  onRun: (cmd: PaletteCommand) => void;
  onHover: (id: string) => void;
}

/** The note under the rows: why the search part is missing or still coming. */
function statusMessage(status: SearchStatus, hasRows: boolean): string | null {
  if (status === 'error') return SEARCH_FAILED_MESSAGE;
  if (status === 'loading' && !hasRows) return 'Searching…';
  if (!hasRows && status !== 'loading') return NO_MATCHES_MESSAGE;
  return null;
}

/** The palette's grouped result list, with its loading, error and empty states. */
export default function PaletteResults({
  listId,
  sections,
  status,
  activeId,
  theme,
  onRun,
  onHover,
}: PaletteResultsProps) {
  const t = NAV_THEMES[theme];
  const message = statusMessage(status, sections.length > 0);

  return (
    <div style={{ padding: '6px 0', maxHeight: 400, overflow: 'auto' }}>
      <div id={listId} role="listbox" aria-label="Search results">
        {sections.map((section) => (
          <div key={section.id} role="group" aria-label={section.title}>
            <div
              role="presentation"
              className="flex items-center justify-between"
              style={{
                padding: '8px 16px 4px',
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: t.subtle,
              }}
            >
              <span>{section.title}</span>
              {section.count && <span style={{ fontWeight: 400 }}>{section.count}</span>}
            </div>
            {section.rows.map((row) => (
              <PaletteRow
                key={row.id}
                command={row}
                optionId={`${listId}-${row.id}`}
                active={row.id === activeId}
                theme={theme}
                onRun={() => onRun(row)}
                onHover={() => onHover(row.id)}
              />
            ))}
          </div>
        ))}
      </div>
      {message && (
        <div
          role="status"
          style={{ padding: '24px 16px', fontSize: 12.5, color: t.subtle, textAlign: 'center' }}
        >
          {message}
        </div>
      )}
    </div>
  );
}
